import {
  ActionButton,
  Breadcrumbs,
  Card,
  CardsGrid,
  Chips,
  PageContent,
  PageHeader,
  SearchBar,
  Spinner,
} from '@campx-org/shared'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Alert, Box, Chip, Stack, Typography } from '@mui/material'
import { NoDataComponent } from 'components/NoDataComponent'
import { ReactNode, useState } from 'react'
import { useQuery } from 'react-query'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { EXAMS } from 'services'
import { PaginationFooter } from './PaginationFooter'
import { BreadcrumbLink, Bundle } from './types'
import { DEFAULT_PAGE_SIZE } from './utils'

type BundleType = 'Evaluation' | 'Revaluation'

interface BundleListRendererProps {
  bundles: Bundle[]
  totalCount: number
  page: number
  limit: number
  isLoading: boolean
  isError: boolean
  breadcrumbs: BreadcrumbLink[]
  bundleTypeFilter?: ReactNode
  noDataMessage?: string
  onBundleSelect: (bundle: Bundle) => void
  onSearch: (val: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onRefetch: () => void
}

function BundleListRenderer({
  bundles,
  totalCount,
  page,
  limit,
  isLoading,
  isError,
  breadcrumbs,
  bundleTypeFilter,
  noDataMessage,
  onBundleSelect,
  onSearch,
  onPageChange,
  onLimitChange,
  onRefetch,
}: BundleListRendererProps) {
  return (
    <>
      <PageHeader title={<Breadcrumbs links={breadcrumbs} />} />

      <PageContent>
        <Stack spacing={2}>
          {bundleTypeFilter && (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="flex-start"
              alignItems="center"
              spacing={2}
            >
              {bundleTypeFilter}
            </Stack>
          )}

          <Box sx={{ maxWidth: 400 }}>
            <SearchBar
              placeholder="Search bundles..."
              onSearch={onSearch}
              textFieldProps={{ size: 'small' }}
            />
          </Box>

          {isError && (
            <Alert
              severity="error"
              action={
                <ActionButton
                  size="small"
                  variant="text"
                  onClick={() => onRefetch()}
                >
                  Retry
                </ActionButton>
              }
            >
              Failed to load bundles.
            </Alert>
          )}

          {isLoading ? (
            <Spinner />
          ) : bundles.length > 0 ? (
            <CardsGrid cardWidth={360} gap={16}>
              {bundles.map((bundle) => (
                <Card
                  key={bundle.id ?? bundle.evaluationBundleId}
                  title={`Bundle No: ${bundle.bundleSerialNo}`}
                  subheader={
                    <Typography color="text.secondary" variant="body2">
                      Booklets: {bundle.bookletCount || 0}
                    </Typography>
                  }
                  footer={{
                    buttonText: 'View Booklets',
                    endIcon: <ChevronRightIcon />,
                    onClick: () => onBundleSelect(bundle),
                  }}
                >
                  <Stack spacing={1}>
                    {(bundle.evaluators || []).map((item) => (
                      <Stack
                        key={`${bundle.bundleSerialNo}-${item.evaluatorType}-${item.evaluator?.fullName}`}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {item.evaluator?.fullName || '-'}
                        </Typography>
                        <Chip label={item.evaluatorType} size="small" />
                      </Stack>
                    ))}
                  </Stack>
                </Card>
              ))}
            </CardsGrid>
          ) : (
            <NoDataComponent message={noDataMessage ?? 'No bundles found.'} />
          )}

          {!isLoading && totalCount > 0 && (
            <PaginationFooter
              page={page}
              limit={limit}
              totalCount={totalCount}
              onPageChange={onPageChange}
              onLimitChange={onLimitChange}
            />
          )}
        </Stack>
      </PageContent>
    </>
  )
}

export default function AdminBundleListPage() {
  const navigate = useNavigate()
  const { examGroupId } = useParams()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const [bundleType, setBundleType] = useState<BundleType>('Evaluation')
  const [search, setSearch] = useState('')
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)
  const [offset, setOffset] = useState(0)

  const page = Math.floor(offset / limit) + 1

  const handleSearch = (val: string) => {
    setSearch(val === '' ? '' : val)
    setOffset(0)
  }

  const handlePageChange = (newPage: number) => {
    setOffset((newPage - 1) * limit)
  }

  const subjectCode = searchParams.get('subjectCode') || ''
  const date = searchParams.get('date') || undefined
  const fromTimeString = searchParams.get('fromTimeString') || undefined
  const toTimeString = searchParams.get('toTimeString') || undefined
  const subjectName = searchParams.get('subjectName') || subjectCode
  const examGroup = location.state?.examGroup

  const queryParams = {
    examCategory: 'external' as const,
    subjectCode,
    date,
    fromTimeString,
    toTimeString,
    bundleType,
    search: search || undefined,
    limit,
    offset,
  }

  const dependencies = [
    examGroupId,
    subjectCode,
    date,
    fromTimeString,
    toTimeString,
    bundleType,
    search,
    limit,
    offset,
  ]

  const { data, isLoading, isError, refetch } = useQuery(
    ['admin-bundles', ...dependencies],
    () =>
      EXAMS.adminBundleEvaluation.getBundles(Number(examGroupId), queryParams),
    { enabled: !!examGroupId && !!subjectCode },
  )

  const bundles: Bundle[] = data?.bundles || []
  const totalCount: number = data?.count ?? 0

  const breadcrumbs: BreadcrumbLink[] = [
    { name: 'Exams', to: '/admin/examinations' },
    {
      name:
        data?.groupName ||
        (examGroup as { groupName?: string } | undefined)?.groupName ||
        'Examination',
      to: `/admin/examinations/${examGroupId}/subjects`,
    },
    { name: subjectCode, to: null },
  ]

  const handleBundleSelect = (bundle: Bundle) => {
    navigate(
      `/admin/examinations/${examGroupId}/bundles/${bundle.id}/booklets`,
      { state: { bundle, subjectName } },
    )
  }

  const bundleTypeFilter = (
    <Chips
      list={[
        { label: 'Evaluation', value: 'Evaluation' },
        { label: 'Revaluation', value: 'Revaluation' },
      ]}
      selected={bundleType}
      onSelected={(value) => {
        if (!value) return
        setBundleType(value as BundleType)
        setOffset(0)
      }}
    />
  )

  return (
    <BundleListRenderer
      bundles={bundles}
      totalCount={totalCount}
      page={page}
      limit={limit}
      isLoading={isLoading}
      isError={isError}
      breadcrumbs={breadcrumbs}
      bundleTypeFilter={bundleTypeFilter}
      noDataMessage={
        bundleType === 'Revaluation'
          ? 'No revaluation bundles found for this subject.'
          : 'No bundles found.'
      }
      onBundleSelect={handleBundleSelect}
      onSearch={handleSearch}
      onPageChange={handlePageChange}
      onLimitChange={(newLimit) => {
        setLimit(newLimit)
        setOffset(0)
      }}
      onRefetch={refetch}
    />
  )
}

export function AdminInternalBundleListPage() {
  const navigate = useNavigate()
  const { examGroupId } = useParams()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const [search, setSearch] = useState('')
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)
  const [offset, setOffset] = useState(0)

  const page = Math.floor(offset / limit) + 1

  const handleSearch = (val: string) => {
    setSearch(val === '' ? '' : val)
    setOffset(0)
  }

  const handlePageChange = (newPage: number) => {
    setOffset((newPage - 1) * limit)
  }

  const evaluationId = searchParams.get('evaluationId') || ''
  const groupId = searchParams.get('groupId') || ''
  const subjectName = searchParams.get('subjectName') || evaluationId
  const examGroup = location.state?.examGroup as
    | { groupName?: string }
    | undefined

  const queryParams = {
    examCategory: 'internal' as const,
    evaluationId: evaluationId || undefined,
    group1Key: groupId || undefined,
    search: search || undefined,
    limit,
    offset,
  }

  const dependencies = [
    examGroupId,
    evaluationId,
    groupId,
    search,
    limit,
    offset,
  ]

  const { data, isLoading, isError, refetch } = useQuery(
    ['admin-bundles', ...dependencies],
    () =>
      EXAMS.adminBundleEvaluation.getBundles(Number(examGroupId), queryParams),
    { enabled: !!examGroupId && !!evaluationId },
  )

  const bundles: Bundle[] = data?.bundles || []
  const totalCount: number = data?.count ?? 0

  const breadcrumbs: BreadcrumbLink[] = [
    { name: 'Exams', to: '/admin/examinations' },
    {
      name: data?.groupName || examGroup?.groupName || 'Examination',
      to: `/admin/internal/${examGroupId}/subjects`,
    },
    { name: subjectName || evaluationId, to: null },
  ]

  const handleBundleSelect = (bundle: Bundle) => {
    const bundleId = bundle.evaluationBundleId || bundle.id
    const sp = new URLSearchParams()
    if (evaluationId) sp.set('evaluationId', evaluationId)
    navigate(
      `/admin/internal/${examGroupId}/bundles/${bundleId}/booklets?${sp.toString()}`,
      { state: { bundle, subjectName, examGroup, examCategory: 'internal' } },
    )
  }

  return (
    <BundleListRenderer
      bundles={bundles}
      totalCount={totalCount}
      page={page}
      limit={limit}
      isLoading={isLoading}
      isError={isError}
      breadcrumbs={breadcrumbs}
      onBundleSelect={handleBundleSelect}
      onSearch={handleSearch}
      onPageChange={handlePageChange}
      onLimitChange={(newLimit) => {
        setLimit(newLimit)
        setOffset(0)
      }}
      onRefetch={refetch}
    />
  )
}
